import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import ifav from './ifav.png';
import energyComponentsTable from '../../assets/images/BBP_CBP_consumption.png';
import { Card, Modal, Button, Popover, OverlayTrigger } from 'react-bootstrap';
import PopoverComponent from '../popover';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../../assets/images/zoom-out-2.svg';
import { useResetContext } from '../ResetContext';
import { useFilterContext } from '../FilterContext';
import DownloadButtonComponent from '../DownloadButtonComponent';
import AIToggleButton from '../AIToggleButton';

const Energy_consumption_bbp = ({ value }) => {
  const { resetdata } = useResetContext();
  const [energy_consumption, set_energy_consumption] = useState(null);
  const [downloadData, setDownloadData] = useState(JSON.parse(value.energy_consumption_BBP[2]));
  const radio_query = useGraphRadio();
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [avg_value, setavg_value] = useState(
    value?.energy_consumption_BBP?.[1]?.average || 'NAN'
  );
  const unit = 'kW/h';
  const [chartType, setChartType] = useState("Bar");
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showZoomModal, setShowZoomModal] = useState(false);

  const popover = (
    <Popover id="popover-basic" style={{ maxWidth: '800px' }}>
      <Popover.Body>
        <div dangerouslySetInnerHTML={{
          __html: `<b>Energy Consumption of Battery Generation BBP:</b> The energy consumption of battery generation BBP is displayed with its components. This KPI is split up among BBP and CBP because the available components of energy consumption differ among the battery generations.<br/><br/>
          
          <b>Available Components for BBP and CBP:</b><br/>
          <img src="${energyComponentsTable}" alt="Energy Consumption Components" style="width: 200%; max-width: 700px; margin: 10px 0;"/><br/>
          
          <b>Time Series Data:</b><br/>
          If multiple vehicles are selected average values are displayed. Days without data are not taken into account for the calculation.<br/><br/>
          
          The <b>average value</b> displayed refers to the <b>total energy consumption</b> (sum of all components).<br/><br/>
          
          <b>Histogram:</b><br/>
          In <b>histogram and boxplot</b> only the <b>total energy consumption</b> (sum of all components) is displayed.<br/>
          Each bar represents the number of days that fall within a certain range (bin) of energy consumption values.<br/>
          All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/>
          
          <b>Boxplot:</b><br/>
          In <b>histogram and boxplot</b> only the <b>total energy consumption</b> (sum of all components) is displayed.<br/>
          The boxplot shows the <b>distribution of daily energy consumption values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>
          • The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>
          • The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>
          • The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box.`
        }} />
      </Popover.Body>
    </Popover>
  );

  // Fetch segmented chart data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Energy consumption bbp",
          category: resetdata
        });
        if (chartType === 'Bar') {
          set_energy_consumption(JSON.parse(data[0]));
          setavg_value(data[1]?.average);
          setDownloadData(JSON.parse(data[3]));
        } else {
          set_energy_consumption(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, value?.vin_short]);

  // Fetch when radio changes
  useEffect(() => {
    if (radio_buttons_test) {
      setIsLoading(true);
      radio_query({
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: 'Energy consumption bbp',
        startDate,
        endDate
      }).then(data => {
        set_energy_consumption(JSON.parse(data['jsonData'][0]));
        setDownloadData(JSON.parse(data['jsonData'][2]));
        setavg_value(data['jsonData'][1]?.average);
        setIsLoading(false);
      });
    }
  }, [radio_buttons_test, value?.vin_short, startDate, endDate]);

  useEffect(() => {
    if (!energy_consumption && value?.energy_consumption_BBP?.[0]) {
      try {
        set_energy_consumption(JSON.parse(value.energy_consumption_BBP[0]));
      } catch (error) {
        console.error("Error parsing fallback energy_consumption JSON", error);
      }
    }
  }, [value, energy_consumption]);

  const handleSelectChange1 = (event) => {
    set_Radio_values((prevValues) => ({
      ...prevValues,
      [event.detail.name]: event.detail.value,
    }));
  };

  const handleToggleModal = () => setShowModal(!showModal);

  return (
    <Card className="card_ride">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <ManText className="card-title-text" variant="component-l" as="span" size="l">
            Energy consumption BBP
          </ManText>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexDirection: "row-reverse" }}>
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img className="man-logovv1" src={ifav}/>
          </OverlayTrigger>
          <Button
            variant="link"
            onClick={() => setShowZoomModal(true)}
            style={{ padding: 0, lineHeight: 0 }}
            title="Enlarge Chart"
          >
            <img src={Zoom} alt="Zoom Icon" width="24" height="20" />
          </Button>
          <DownloadButtonComponent data={downloadData} fileName='energy_consumption_bbp_data.csv' />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginTop: '16px' }}>
        {chartType === 'Bar' ? (
          <AverageDisplay averages={avg_value} unit="kWh/100km" />
        ) : (
          <div style={{ width: '150px' }} />
        )}

        {chartType === 'Bar' ? (
          <ManRadioButtonGroup
            name="Energy consumption bbp"
            invalid="false"
            onManChange={handleSelectChange1}
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <ManRadioButton name="Energy consumption bbp" label="Daily" value="Daily" checked />
            <ManRadioButton name="Energy consumption bbp" label="Monthly" value="Monthly" />
            <ManRadioButton name="Energy consumption bbp" label="Yearly" value="Yearly" />
          </ManRadioButtonGroup>
        ) : (
          <div style={{ flexGrow: 1 }} />
        )}

        <div style={{ flexShrink: 0 }}>
          <SegmentedControl selected={chartType} onChange={(type) => setChartType(type)} />
        </div>
      </div>

      {isLoading ? (
        <Shimmer_graph />
      ) : !energy_consumption || !energy_consumption.data || energy_consumption.data.length === 0 ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          textAlign: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '5px',
          background: '#fafafa',
          marginTop: '10px'
        }}>
          <div>
            <ManText variant="headline-m" as="div" style={{ color: '#59636D', marginBottom: '8px' }}>
              No Data Available
            </ManText>
            <ManText variant="body-m" as="div" style={{ color: '#838a92' }}>
              There is no data available for the selected filters
            </ManText>
          </div>
        </div>
      ) : (
        <Plot
          data={energy_consumption.data}
          layout={{
            ...energy_consumption.layout,
            margin: { t: 10, r: 10, l: 50, b: 280 }, // Increased bottom margin for legend space
            legend: {
              ...energy_consumption.layout.legend,
              orientation: 'h',
              x: 0.5,
              xanchor: 'center',
              y: -0.55, // Position legend further down
              yanchor: 'top',
              bgcolor: 'rgba(255,255,255,0.8)',
              bordercolor: 'rgba(0,0,0,0)',
              borderwidth: 0
            },
            autosize: true,
            responsive: true,
          }}
          useResizeHandler={true}
          style={{ width: '100%', height: '480px' }} // Increased height to accommodate legend
        />
      )}

      {/* 🔥 AI Toggle Button - Only show for Bar charts with data or quality annotations */}
      {chartType === 'Bar' && energy_consumption && energy_consumption.data && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          paddingLeft: '10px',
          marginTop: '8px',
          marginBottom: '8px'
        }}>
          <AIToggleButton
            downloadData={downloadData}
            radioButtonState={radio_buttons_test || { 'Energy consumption bbp': 'Daily' }}
            kpiName="Energy Consumption BBP"
            chartType={chartType}
            vinShort={value.vin_short}
            title="AI | Automotive Intelligence - Energy Consumption BBP Analysis"
          />
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download Unavailable</Modal.Title>
        </Modal.Header>
        <Modal.Body>Data is not available for download at the moment.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showZoomModal} onHide={() => setShowZoomModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Energy consumption BBP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {energy_consumption && (
            <Plot
              data={energy_consumption.data}
              layout={{
                ...energy_consumption.layout,
                height: 650, // Increased height for better legend spacing
                width: 760,
                margin: { t: 20, r: 20, l: 60, b: 300 }, // Increased margins for better legend spacing
                legend: {
                  ...energy_consumption.layout.legend,
                  orientation: 'h',
                  x: 0.5,
                  xanchor: 'center',
                  y: -0.5, // Position legend further down for modal
                  yanchor: 'top',
                  bgcolor: 'rgba(255,255,255,0.8)',
                  bordercolor: 'rgba(0,0,0,0)',
                  borderwidth: 0
                },
                autosize: true
              }}
              useResizeHandler={true}
              style={{ width: '100%', height: '100%' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowZoomModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

Energy_consumption_bbp.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string,
    energy_consumption_BBP: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
  }).isRequired,
};

export default Energy_consumption_bbp;
